import React, { useState, useEffect } from 'react';
import { Lock, ArrowRight, AlertCircle, Loader2, Sparkles, Building } from 'lucide-react';
import { db } from '../services/db';
import { ClientEntity } from '../types';
import { ClientBranding, extractBrandingFromHtml } from '../utils/brandExtractor';

interface ClientLoginProps {
  onLogin: (client: ClientEntity) => void;
  presetSlug?: string | null;
}

export const ClientLogin: React.FC<ClientLoginProps> = ({ onLogin, presetSlug }) => {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  /**
   * A mensagem quando a causa NAO e credencial errada.
   *
   * A tela tinha um booleano so: qualquer excecao virava "usuario ou PIN
   * incorretos". Enquanto `clientes.obter` era `acesso: 'operador'`, o login
   * acertava a senha, criava a sessao e morria no 403 seguinte — e a pessoa lia
   * que a senha estava errada. Duas causas diferentes com a mesma mensagem
   * mandam quem tenta resolver para o lado errado, e foi o que aconteceu.
   */
  const [falha, setFalha] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [clientBranding, setClientBranding] = useState<ClientBranding | null>(null);
  const [previewClient, setPreviewClient] = useState<ClientEntity | null>(null);

  // If a slug or username query param was passed (e.g. ?client=slug or #/login/slug)
  useEffect(() => {
    const checkTargetClient = async () => {
      const hash = window.location.hash;
      let targetSlug = presetSlug;
      
      if (!targetSlug && hash.startsWith('#/login/')) {
        targetSlug = hash.replace('#/login/', '').split('?')[0];
      }

      if (!targetSlug) {
        const urlParams = new URLSearchParams(window.location.search);
        targetSlug = urlParams.get('cliente') || urlParams.get('client') || urlParams.get('slug');
      }

      if (targetSlug) {
        try {
          const clientData = await db.getClientBySlug(targetSlug);
          if (clientData) {
            setPreviewClient(clientData);
            const savedBranding = localStorage.getItem(`client_branding_${clientData.id}`);
            if (savedBranding) {
              setClientBranding(JSON.parse(savedBranding));
            } else {
              const brand = extractBrandingFromHtml(clientData.htmlContent, clientData.data);
              setClientBranding(brand);
            }
            if (clientData.data?.username) {
              setUsername(clientData.data.username);
            }
          }
        } catch (e) {
          console.warn('Error fetching client by slug for branded login', e);
        }
      }
    };
    checkTargetClient();
  }, [presetSlug]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !pin) {
      setError(true);
      return;
    }

    setLoading(true);
    setError(false);
    setFalha(null);

    try {
      const client = await db.loginClient(username, pin);

      if (client) {
        onLogin(client);
      } else {
        // `loginClient` devolve `undefined` SO para 401 e 429 — as duas respostas
        // que de fato significam "credencial ou tentativas". Qualquer outra coisa
        // vem como excecao, abaixo.
        setError(true);
      }
    } catch (err) {
      console.error('[login] falha que nao e credencial:', err);
      setFalha(
        err instanceof Error && err.message
          ? err.message
          : 'Nao consegui completar o acesso. Tente de novo em instantes.',
      );
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = clientBranding?.primaryColor || '#18181b';
  const logoUrl = clientBranding?.logoUrl;
  const businessName = previewClient?.data?.name || clientBranding?.businessName || 'Painel de Controle';

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4 font-sans relative">
      
      {/* Botão Voltar Navbar Simples */}
      <div className="absolute top-6 left-6">
        <button
          onClick={() => {
            if (previewClient) {
              window.location.hash = `#/site/${previewClient.slug}`;
            } else {
              window.location.hash = '';
            }
          }}
          className="text-gray-500 hover:text-black font-medium text-xs sm:text-sm flex items-center gap-1.5 transition-colors"
        >
          ← Voltar ao site oficial
        </button>
      </div>

      <div className="bg-white max-w-md w-full rounded-2xl shadow-xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Top Header with Custom Brand or Default */}
        <div className="p-8 text-center border-b border-gray-100 bg-gradient-to-b from-gray-50/60 to-white">
          {logoUrl ? (
            <div className="h-16 max-w-[200px] mx-auto flex items-center justify-center mb-3">
              <img src={logoUrl} alt={businessName} className="max-h-full max-w-full object-contain" />
            </div>
          ) : (
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 text-white shadow-md transition-colors"
              style={{ backgroundColor: primaryColor }}
            >
              {clientBranding ? (
                <span className="font-bold text-2xl">{clientBranding.brandInitial}</span>
              ) : (
                <Lock size={24} />
              )}
            </div>
          )}

          <h1 className="text-xl font-bold text-gray-900 tracking-tight">{businessName}</h1>
          <p className="text-gray-500 text-xs mt-1">Acesso Restrito & Cockpit de Gestão</p>
        </div>

        <div className="p-8">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                Usuário / Acesso
              </label>
              <input 
                type="text" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)} 
                className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-all ${
                  error ? 'border-red-500 focus:ring-1 focus:ring-red-500' : 'border-gray-300 focus:border-black focus:ring-1 focus:ring-black'
                }`} 
                placeholder="ex: seunome" 
                autoFocus 
              />
            </div>

            <div>
              {/*
                "PIN" virou "Senha": o campo aceita letras (o schema do contrato
                vai ate 72 caracteres) e o que a equipe entrega hoje e uma senha,
                nao quatro digitos. Chamar de PIN fazia quem recebeu uma senha com
                letras achar que estava na tela errada.
              */}
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                Senha
              </label>
              <input 
                type="password" 
                value={pin} 
                onChange={(e) => setPin(e.target.value)} 
                autoComplete="current-password" 
                className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-all ${
                  error ? 'border-red-500 focus:ring-1 focus:ring-red-500' : 'border-gray-300 focus:border-black focus:ring-1 focus:ring-black'
                }`} 
                placeholder="Sua senha" 
              />
              {error && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1 animate-in fade-in">
                  <AlertCircle size={13} /> Usuário ou senha incorretos.
                </p>
              )}
              {falha && (
                <p className="text-xs text-amber-700 mt-2 flex items-start gap-1 animate-in fade-in">
                  <AlertCircle size={13} className="shrink-0 mt-0.5" />
                  <span>
                    <b>A senha está certa, mas algo mais falhou.</b> {falha}
                  </span>
                </p>
              )}
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-70 shadow-md hover:opacity-90 active:scale-[0.99] text-sm"
              style={{ backgroundColor: primaryColor }}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : 'Entrar no Sistema'} <ArrowRight size={16} />
            </button>
          </form>

          {/* Demonstration Notice if no slug */}
          {!previewClient && (
            <div className="mt-6 pt-6 border-t border-gray-100 text-center">
              <p className="text-[11px] text-gray-400">
                Acesso com credenciais fornecidas no onboarding da sua empresa.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
